// Authentication state hook — tenant-scoped
import { useState, useEffect, useCallback, useRef } from 'react';
import { logActivity } from '../api/activityLogger.js';
import { getFunctionUrl } from '../api/functionUrl.js';
import {
  setSessionToken,
  getSessionToken,
  setTenantId as setClientTenantId,
} from '../api/azureClient.js';
import { resolveTenant } from '../tenant/tenantConfig.js';
import { clearAllDraftsForUser } from '../documents/draftStorage.js';
import { clearTeardownBackup } from './teardownBackup.js';

// Sliding session: refresh the JWT when it's within this window of expiry.
// Refresh after 22h (with 24h token life), so active users stay logged in.
const SESSION_REFRESH_THRESHOLD_MS = 22 * 60 * 60 * 1000;
// Check every 15 minutes whether we need to refresh
const SESSION_CHECK_INTERVAL_MS = 15 * 60 * 1000;

import { loadTenantUsers, saveTenantUsers } from '../api/tenantApi.js';

/** id → JSON of the record as the server last confirmed it. */
function snapshotUsers(list) {
  const map = new Map();
  for (const u of list || []) {
    if (u && u.id) map.set(u.id, JSON.stringify(u));
  }
  return map;
}

export function useAuth(tenantId) {
  const [users, setUsers] = useState([]);
  const [session, setSession] = useState(null);
  const [authReady, setReady] = useState(false);

  // Last server-confirmed shape of every user this browser knows about. Saves
  // post only the records that differ from this, so a routine save never
  // resubmits a stale copy of somebody else's record — which is both what used
  // to erase concurrently-created users and what would now 409 against their
  // per-record version (DB-CONC-002).
  const serverUsersRef = useRef(new Map());

  // True once the user list for this session has been read (or the read has
  // failed). Gates that derive a permission from the signed-in user's own
  // record (hrRecruitmentAccess, GH #7057) wait on it instead of reading an
  // empty list as "no permission" — and a failed read counts as loaded with
  // no record, so the gate fails closed rather than waiting forever.
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Load tenant users when authenticated
  useEffect(() => {
    if (!tenantId || !session) return;

    setUsersLoaded(false);
    loadTenantUsers(tenantId)
      .then((u) => {
        setUsers(u);
        serverUsersRef.current = snapshotUsers(u);
      })
      .catch((e) => {
        console.error('[useAuth] failed to load tenant users:', e.message);
      })
      .finally(() => setUsersLoaded(true));
  }, [tenantId, session]);

  // Load session from localStorage on mount, with handoff-code exchange first.
  useEffect(() => {
    (async () => {
      // ── Phase 2: exchange a #code= handoff token from login.tryggr.dk ─────
      // Check before touching localStorage so that a fresh handoff always wins.
      try {
        const hash = window.location.hash || '';
        const m = hash.match(/[#&]code=([^&]+)/);
        if (m) {
          const code = decodeURIComponent(m[1]);
          // Clear the hash immediately so a page refresh cannot re-use the code.
          history.replaceState(null, '', window.location.pathname + window.location.search);

          const exchangeUrl = getFunctionUrl('auth-handoff');
          const res = await fetch(exchangeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'exchange', code }),
          });

          if (res.ok) {
            const { token, user } = await res.json();
            if (token && user) {
              const firstName = user.firstName || (user.displayName || '').split(' ')[0] || '';
              const lastName =
                user.lastName || (user.displayName || '').split(' ').slice(1).join(' ') || '';
              const displayName =
                [firstName, lastName].filter(Boolean).join(' ') || user.displayName || '';
              const sess = {
                userId: user.id,
                username: user.username,
                firstName,
                lastName,
                displayName,
                role: user.role,
                themeId: user.themeId || null,
              };
              localStorage.setItem(`seasuite_token_${tenantId}`, token);
              localStorage.setItem(`seasuite_session_${tenantId}`, JSON.stringify(sess));
              setSessionToken(token);
              setClientTenantId(tenantId);
              tokenIssuedAt.current = decodeJwtIssuedAt(token) || Date.now();
              setSession(sess);
              setTimeout(() => {
                logActivity(tenantId, sess, { module: 'Auth', actionType: 'login' });
              }, 0);
              setReady(true);
              return; // skip localStorage restore — session already set
            }
          }
        }
      } catch (e) {
        console.warn('[useAuth] handoff exchange failed:', e.message);
        // Fall through to normal localStorage restore
      }

      // ── Normal session restore from localStorage ───────────────────────────
      try {
        const stored = localStorage.getItem(`seasuite_session_${tenantId}`);
        if (stored) {
          const sess = JSON.parse(stored);
          const token = localStorage.getItem(`seasuite_token_${tenantId}`);
          if (token) {
            setSessionToken(token);
            setClientTenantId(tenantId);
            tokenIssuedAt.current = decodeJwtIssuedAt(token);
            setSession(sess);
          }
        }
      } catch (e) {
        console.warn('[useAuth] failed to restore session:', e.message);
      }
      setReady(true);
    })();
  }, [tenantId]);

  // Track when the current session token was issued (for sliding refresh)
  const tokenIssuedAt = useRef(null);

  // Optional pre-logout flush — caller wires this to usePersistence.flushPendingSave
  // so any pending debounced save completes before the session is torn down.
  const preLogoutFlushRef = useRef(null);

  // Decode a JWT payload without verifying. We never trust this for security
  // decisions — only for client-side timing and for reading the tenant binding
  // the server already validated. Returns null if the token is malformed.
  function decodeJwtPayload(token) {
    try {
      return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      return null;
    }
  }

  // iat (ms) for the sliding refresh timer. Returns null if not decodable.
  function decodeJwtIssuedAt(token) {
    const payload = decodeJwtPayload(token);
    return payload && typeof payload.iat === 'number' ? payload.iat * 1000 : null;
  }

  // Listen for session-expired events from azureClient (401 on API calls)
  useEffect(() => {
    function onSessionExpired() {
      console.warn('[useAuth] Session expired — logging out');
      localStorage.removeItem(`seasuite_token_${tenantId}`);
      localStorage.removeItem(`seasuite_session_${tenantId}`);
      // azureClient's own handleSessionExpired() nulls _sessionToken but leaves
      // _tenantId set, so the module kept a tenant binding for a session that
      // no longer exists. Harmless today because every request also needs the
      // token, but it is a stale identity sitting in a module singleton across
      // a tenant switch — clear it with the rest of the teardown.
      setSessionToken(null);
      setClientTenantId(null);
      setSession(null);
      tokenIssuedAt.current = null;
    }
    window.addEventListener('seasuite:session-expired', onSessionExpired);
    return () => window.removeEventListener('seasuite:session-expired', onSessionExpired);
  }, [tenantId]);

  // F-36: cross-tab stale session. `storage` only fires in OTHER tabs/windows
  // when localStorage changes — it never fires in the tab that made the write.
  // Without this, a 401 handled in one tab (azureClient clears localStorage and
  // fires 'seasuite:session-expired' locally) leaves every other open tab with
  // a stale in-memory session and a stale module-level _sessionToken in
  // azureClient, so those tabs keep issuing requests with a dead token. Tear
  // the session down in this tab whenever another tab removes or replaces the
  // token for the current tenant.
  useEffect(() => {
    const tokenKey = `seasuite_token_${tenantId}`;
    const sessionKey = `seasuite_session_${tenantId}`;
    function onStorage(e) {
      // A whole-store clear (localStorage.clear() in another tab) is reported
      // as e.key === null, with no oldValue/newValue — so the key comparison
      // below swallowed it, and the ONE teardown that removes everything was
      // the one this tab ignored. That leaves exactly the stale in-memory
      // session and dead-token request loop F-36 exists to prevent (#5008).
      if (e.key === null) {
        // Nothing to tear down if this tab never had a session — a clear()
        // fired by some unrelated code must not churn a logged-out tab.
        if (!getSessionToken() && !localStorage.getItem(tokenKey)) return;
      } else if (e.key === sessionKey) {
        // The session RECORD going away is a teardown too, and this listener
        // used to ignore it entirely — it matched only the token key, so a tab
        // (or an admin, or a support script) clearing `seasuite_session_*`
        // left every other tab logged in and working (#5363). CLAUDE.md
        // documents both keys as the session's storage; watching one of them
        // covered half the teardown surface.
        //
        // Removal only. A session record being REPLACED is an ordinary
        // re-login or a profile refresh, and the token write that accompanies
        // it is already handled by the branch below — tearing down on every
        // rewrite of this key would log people out of their own fresh session.
        if (e.newValue != null) return;
        if (e.oldValue == null) return;
      } else {
        if (e.key !== tokenKey) return;
        if (e.newValue === e.oldValue) return;
        // A token being REPLACED is not by itself a teardown, and treating it
        // as one broke the sliding session it was meant to protect: after
        // SESSION_REFRESH_THRESHOLD_MS whichever tab notices first calls
        // session-refresh and writes the renewed token, and every other tab
        // on the same tenant saw that write and logged itself out — the exact
        // opposite of "active users stay logged in indefinitely". The sibling
        // sessionKey branch above already makes this distinction ("a
        // replacement means another tab just signed someone in"); the token
        // branch never did.
        //
        // The question a replacement actually poses is WHOSE session this now
        // is. Same subject → the same session, renewed: adopt the new token so
        // this tab's next request carries it, and keep the session. Different
        // subject (someone signed in as another user on this tenant), or a
        // token neither side can decode → fall through to the teardown below,
        // which is F-36's original case and stays unchanged.
        if (e.newValue != null) {
          const nextSub = decodeJwtPayload(e.newValue)?.sub;
          const prevSub = decodeJwtPayload(e.oldValue || getSessionToken() || '')?.sub;
          if (nextSub && prevSub && nextSub === prevSub) {
            setSessionToken(e.newValue);
            tokenIssuedAt.current = decodeJwtIssuedAt(e.newValue) || Date.now();
            return;
          }
        }
      }
      console.warn('[useAuth] Session ended in another tab — logging out here too');
      // Sweep up the OTHER half of the pair, but only when what we observed was
      // a removal. A tab that sees one key vanish must not leave the other —
      // the previous user's token, or their name and role — sitting in storage
      // on a shared device. But a replacement means another tab just signed
      // someone in and wrote both keys itself, and deleting what it wrote would
      // log that tab out on its next reload.
      if (e.key === null || e.newValue == null) {
        try {
          localStorage.removeItem(sessionKey);
          localStorage.removeItem(tokenKey);
        } catch {
          /* storage unavailable — the in-memory teardown below still runs */
        }
      }
      setSessionToken(null);
      setClientTenantId(null);
      setSession(null);
      tokenIssuedAt.current = null;
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [tenantId]);

  // Sliding session: periodically refresh the JWT before it expires
  useEffect(() => {
    if (!session || !tenantId) return;

    // Fall back to now() only when iat could not be decoded. A real iat-based
    // value lets the very first check below catch tokens that are already
    // close to expiry when the tab is reopened.
    if (!tokenIssuedAt.current) {
      const token = localStorage.getItem(`seasuite_token_${tenantId}`);
      tokenIssuedAt.current = (token && decodeJwtIssuedAt(token)) || Date.now();
    }

    async function tryRefresh() {
      const elapsed = Date.now() - tokenIssuedAt.current;
      if (elapsed < SESSION_REFRESH_THRESHOLD_MS) return;

      try {
        const token = localStorage.getItem(`seasuite_token_${tenantId}`);
        if (!token) return;

        const refreshUrl = getFunctionUrl('session-refresh');
        const res = await fetch(refreshUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) return; // Will be caught by 401 handler if truly expired

        const { token: newToken } = await res.json();
        if (!newToken) {
          console.warn('[useAuth] session refresh returned no token — keeping existing session');
          return;
        }
        localStorage.setItem(`seasuite_token_${tenantId}`, newToken);

        setSessionToken(newToken);
        tokenIssuedAt.current = decodeJwtIssuedAt(newToken) || Date.now();
      } catch (e) {
        console.warn('[useAuth] session refresh failed:', e.message);
      }
    }

    // Run one check immediately so a near-expired token gets refreshed before
    // any module (e.g. the document editor) is opened.
    tryRefresh();
    const interval = setInterval(tryRefresh, SESSION_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [session, tenantId]);

  const persistUsers = useCallback(
    (u) => {
      // Safety guard: never persist an empty users array to the DB.
      // A logged-in session implies at least one user exists; an empty array
      // here means a stale closure or race condition — not a real deletion.
      if (!Array.isArray(u) || u.length === 0) {
        console.warn(
          '[useAuth] persistUsers called with empty array — ignoring to prevent data loss'
        );
        return;
      }
      setUsers(u);
      if (!tenantId || !session) return;

      // Send only the records that actually differ from the server's last
      // confirmed shape. Everything else is left untouched server-side, so two
      // admins working on different users never contend, and a user this
      // browser never fetched is never overwritten or dropped.
      const known = serverUsersRef.current;
      const changed = u.filter((rec) => {
        if (!rec || !rec.id) return false;
        const prev = known.get(rec.id);
        return prev === undefined || prev !== JSON.stringify(rec);
      });
      if (changed.length === 0) return;

      // Fire-and-forget persistence. Catch failures here so a background save
      // (e.g. nav-tracking sidebar prefs) can never bubble up as an unhandled
      // rejection — the global handler in main.jsx would otherwise show a
      // full-screen error overlay and lock the user out of the app.
      saveTenantUsers(tenantId, changed)
        .then((res) => {
          // Adopt the new per-record versions so the NEXT save is not a
          // self-inflicted conflict against a token we ourselves superseded.
          const versions = (res && res.versions) || {};
          for (const rec of changed) {
            const next = versions[rec.id] ? { ...rec, _userVersion: versions[rec.id] } : rec;
            known.set(rec.id, JSON.stringify(next));
          }
          if (Object.keys(versions).length) {
            setUsers((prev) =>
              prev.map((x) => (versions[x.id] ? { ...x, _userVersion: versions[x.id] } : x))
            );
          }
        })
        .catch((e) => {
          console.warn('[useAuth] persistUsers: save failed (non-fatal):', e?.message);
        });
    },
    [tenantId, session]
  );

  /**
   * Internal helper — persists a token+user pair to localStorage, sets the
   * in-memory session token, and fires the session-set state update.
   * Called after successful password login OR after MFA verification.
   */
  const _establishSession = useCallback(
    (token, user) => {
      const firstName = user.firstName || (user.displayName || '').split(' ')[0] || '';
      const lastName =
        user.lastName || (user.displayName || '').split(' ').slice(1).join(' ') || '';
      const displayName = [firstName, lastName].filter(Boolean).join(' ') || user.displayName || '';
      const sess = {
        userId: user.id,
        username: user.username,
        firstName,
        lastName,
        displayName,
        role: user.role,
        themeId: user.themeId || null,
      };
      localStorage.setItem(`seasuite_token_${tenantId}`, token);
      localStorage.setItem(`seasuite_session_${tenantId}`, JSON.stringify(sess));
      setSessionToken(token);
      setClientTenantId(tenantId);
      tokenIssuedAt.current = decodeJwtIssuedAt(token) || Date.now();
      setSession(sess);
      setTimeout(() => {
        logActivity(tenantId, sess, { module: 'Auth', actionType: 'login' });
      }, 0);
    },
    [tenantId]
  );

  /**
   * Log in with username + password (or passwordless for voided-password accounts).
   *
   * Options:
   *   { passwordless: true } — skip the password check; the backend issues an
   *     mfa-challenge token without verifying a password. Use this when the user's
   *     passwordVoided flag is true (after MFA enrolment).
   *
   * Returns a structured result object:
   *
   *   { status: 'ok' }
   *     Session established — LoginScreen will unmount as App.jsx detects the session.
   *
   *   { status: 'grace-period', token, user, loginsRemaining: N }
   *     Session NOT yet established — show grace notice, then call completeLogin().
   *
   *   { status: 'mfa-challenge', method: 'webauthn'|'totp', challengeToken }
   *     Show MfaVerifyStep; session established via completeLogin() on verify success.
   *
   *   { status: 'mfa-setup-required', challengeToken, reason: 'new-user'|'grace-exhausted' }
   *     Show MFA setup flow (Phase 3); session established after enrolment completes.
   *
   *   { status: 'temp-password-expired' }
   *     Temp password has lapsed — user must contact their admin.
   *
   *   { status: 'password-voided' }
   *     Password login disabled post-MFA enrolment — offer passwordless flow.
   *
   *   { status: 'error', error: string }
   *     Login failed — show error message.
   */
  const login = useCallback(
    async (username, password, options = {}) => {
      try {
        const loginUrl = getFunctionUrl('tenant-login');
        const body = { tenantId, username };
        if (options.passwordless) {
          body.passwordless = true;
        } else {
          body.password = password;
        }

        const response = await fetch(loginUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (data.passwordVoided) return { status: 'password-voided' };
          return { status: 'error', error: data.error || 'Incorrect username or password.' };
        }

        const { status } = data;

        if (status === 'ok') {
          _establishSession(data.token, data.user);
          return { status: 'ok' };
        }

        if (status === 'grace-period') {
          // Don't establish the session yet — LoginScreen shows the grace notice
          // first so the user understands their MFA enrolment deadline, then calls
          // completeLogin() when they dismiss it.
          //
          // challengeToken is present — lets the grace-period screen offer immediate
          // MFA setup via MfaSetupModal without requiring a second login.
          return {
            status: 'grace-period',
            token: data.token,
            user: data.user,
            loginsRemaining: data.loginsRemaining,
            challengeToken: data.challengeToken || null,
          };
        }

        if (status === 'mfa-challenge') {
          return {
            status: 'mfa-challenge',
            method: data.method,
            challengeToken: data.challengeToken,
          };
        }

        if (status === 'mfa-setup-required') {
          return {
            status: 'mfa-setup-required',
            challengeToken: data.challengeToken,
            reason: data.reason,
          };
        }

        if (status === 'mfa-recovery-required') {
          // Stranded account: password voided but no authenticator enrolled.
          // The caller routes the user to the email-recovery flow.
          return { status: 'mfa-recovery-required' };
        }

        if (status === 'temp-password-expired') {
          return { status: 'temp-password-expired' };
        }

        return { status: 'error', error: 'Unexpected response from server.' };
      } catch (e) {
        console.error('[useAuth] login error:', e);
        return { status: 'error', error: 'Network error. Please try again.' };
      }
    },
    [tenantId, _establishSession]
  );

  /**
   * Finalise login after the second factor is verified or after the user
   * acknowledges the grace-period notice.
   *
   * Called by:
   *   • MfaVerifyStep.onSuccess(token, user) after WebAuthn or TOTP succeeds
   *   • LoginScreen "Continue" button on the grace-period screen
   *   • Phase 3 MfaSetupModal on enrolment completion
   */
  const completeLogin = useCallback(
    (token, user) => {
      _establishSession(token, user);
    },
    [_establishSession]
  );

  /**
   * Consume a cross-subdomain login handoff code.
   *
   * Phase 1 of the central-login consolidation. A code arrives in the URL
   * fragment (`#code=…`) after the central login bounces the browser to this
   * slug. We exchange it (single-use, <=60s) for the session JWT that was minted
   * on login.tryggr.dk, then establish the session locally.
   *
   * AUTH-B-002 (consumer side): we independently verify the returned JWT's own
   * tenant_id matches THIS slug's tenant before trusting it — never derive the
   * tenant from the exchange response or any URL parameter.
   *
   * Returns { ok: true } or { ok: false, error }.
   */
  const consumeHandoff = useCallback(
    async (code) => {
      try {
        const url = getFunctionUrl('auth-handoff');
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'exchange', code }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok || !data.token) {
          return { ok: false, error: data.error || 'Invalid or expired sign-in code.' };
        }

        const payload = decodeJwtPayload(data.token);
        if (!payload || payload.tenant_id !== tenantId) {
          console.error('[useAuth] handoff tenant mismatch — refusing session');
          return { ok: false, error: 'This sign-in link was issued for a different workspace.' };
        }

        // user is non-authoritative display metadata; fall back to JWT claims.
        const user =
          data.user && typeof data.user === 'object'
            ? data.user
            : { id: payload.sub, username: payload.sub, displayName: '', role: payload.role };

        _establishSession(data.token, user);
        return { ok: true };
      } catch (e) {
        console.error('[useAuth] consumeHandoff error:', e);
        return { ok: false, error: 'Network error. Please try again.' };
      }
    },
    [tenantId, _establishSession]
  );

  const logout = useCallback(async () => {
    // Flush any pending debounced save before clearing the token so an edit
    // made within the 1.5s debounce window is not silently lost (AU-3).
    if (preLogoutFlushRef.current) {
      await preLogoutFlushRef.current().catch(() => {});
    }
    if (session) {
      logActivity(tenantId, session, { module: 'Auth', actionType: 'logout' });
      // Never leave a logged-out user's in-flight document drafts on a shared
      // device/browser for the next person who logs into this tenant to see
      // (F-37 follow-up). Scoped by the subdomain slug — the same synchronous
      // identifier DocumentEditor uses for its draft keys — so this always
      // targets the exact keys that were written, with no dependency on the
      // (async) resolved tenant record.
      clearAllDraftsForUser(resolveTenant().tenantSlug, session.userId);
      // GH #6307 — the same rationale, applied to the strictly larger artefact.
      // A teardown backup is a whole-tenant-state blob taken when a save could
      // not be delivered; leaving one behind on a shared browser let the next
      // person to log into this tenant replay it under their own token, with
      // the activity trail attributing this user's unreviewed edit to them.
      // Keyed by user id (not slug) because the backup is written against the
      // resolved tenant id that usePersistence loaded state for.
      clearTeardownBackup(tenantId, session.userId);
    }
    localStorage.removeItem(`seasuite_token_${tenantId}`);
    localStorage.removeItem(`seasuite_session_${tenantId}`);
    setSessionToken(null);
    setClientTenantId(null);
    tokenIssuedAt.current = null;
    setSession(null);
  }, [session, tenantId]);

  const registerFlushFn = useCallback((fn) => {
    preLogoutFlushRef.current = fn;
  }, []);

  const requestMagicLink = useCallback(
    async (email) => {
      try {
        const magicLinkUrl = getFunctionUrl('employee-send-magic-link');
        const response = await fetch(magicLinkUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tenantId, email }),
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('[useAuth] magic link request failed:', error);
          return { ok: false, error: error.error || 'Failed to send magic link' };
        }

        return { ok: true };
      } catch (e) {
        console.error('[useAuth] magic link request error:', e);
        return { ok: false, error: 'Network error. Please try again.' };
      }
    },
    [tenantId]
  );

  const verifyMagicLink = useCallback(
    async (token) => {
      try {
        const verifyUrl = getFunctionUrl('employee-verify-magic-link');
        const response = await fetch(verifyUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tenantId, token }),
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('[useAuth] magic link verify failed:', error);
          return { ok: false, error: error.error || 'Invalid or expired magic link' };
        }

        const { token: jwt, user } = await response.json();

        // Store in a portal-specific key so the employee portal session does not
        // overwrite or conflict with a regular tenant-user session.
        localStorage.setItem(`seasuite_portal_token_${tenantId}`, jwt);
        localStorage.setItem(
          `seasuite_portal_session_${tenantId}`,
          JSON.stringify({
            employeeId: user.id,
            displayName: user.displayName,
            email: user.email || '',
          })
        );

        return { ok: true, employeeId: user.id };
      } catch (e) {
        console.error('[useAuth] magic link verify error:', e);
        return { ok: false, error: 'Network error. Please try again.' };
      }
    },
    [tenantId]
  );

  return {
    users,
    usersLoaded,
    persistUsers,
    session,
    login,
    completeLogin,
    consumeHandoff,
    logout,
    registerFlushFn,
    authReady,
    requestMagicLink,
    verifyMagicLink,
  };
}
